'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ShopHeaderProps {
  shop: {
    name: string;
    logo: string;
    logoAlt: string;
    rating: number;
    reviewCount: number;
    verified: boolean;
    responseTime: string;
    yearsInBusiness: number;
  };
}

const ShopHeader = ({ shop }: ShopHeaderProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-elevation-md p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
          <div className="w-24 h-24 rounded-lg bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-8 bg-muted rounded w-64" />
            <div className="h-4 bg-muted rounded w-48" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-elevation-md p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
        <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
          <AppImage
            src={shop.logo}
            alt={shop.logoAlt}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-text-primary mb-2">
                {shop.name}
              </h1>
              <div className="flex items-center space-x-4 flex-wrap gap-2">
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Icon
                      key={star}
                      name="StarIcon"
                      size={18}
                      variant={star <= Math.floor(shop.rating) ? 'solid' : 'outline'}
                      className={star <= Math.floor(shop.rating) ? 'text-accent' : 'text-muted-foreground'}
                    />
                  ))}
                  <span className="font-body text-sm font-medium text-text-primary ml-2">
                    {shop.rating.toFixed(1)}
                  </span>
                  <span className="font-body text-sm text-text-secondary">
                    ({shop.reviewCount} reviews)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-wrap gap-2">
            {shop.verified && (
              <div className="flex items-center space-x-2 bg-primary/10 rounded-full px-3 py-1">
                <Icon name="CheckBadgeIcon" size={16} className="text-primary" variant="solid" />
                <span className="font-caption text-xs font-medium text-primary">
                  Verified Shop
                </span>
              </div>
            )}
            <div className="flex items-center space-x-2 bg-muted rounded-full px-3 py-1">
              <Icon name="ClockIcon" size={16} className="text-text-secondary" />
              <span className="font-caption text-xs text-text-secondary">
                Responds in {shop.responseTime}
              </span>
            </div>
            <div className="flex items-center space-x-2 bg-muted rounded-full px-3 py-1">
              <Icon name="BuildingStorefrontIcon" size={16} className="text-text-secondary" />
              <span className="font-caption text-xs text-text-secondary">
                {shop.yearsInBusiness}+ years
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopHeader;